/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil, tap, filter } from 'rxjs/operators';
import { Workflow, WorkflowHost, WorkflowStep } from 'src/app/modules/core/store/models/workflow.model';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import * as fromAuth from 'src/app/modules/authentication/store/auth';

@Component({
    selector: 'topo-workflow-gen-transition-select',
    templateUrl: './workflow-gen-transition-select.component.html',
    styleUrls: ['./workflow-gen-transition-select.component.scss'],
    standalone: false
})
export class WorkflowGeneralTransitionSelectComponent implements OnInit, OnDestroy {
  @Input() set hostObject(value: WorkflowHost) {
    this.hostObject$.next(value);
  }

  hostObject$: BehaviorSubject<WorkflowHost> = new BehaviorSubject(null);

  @Input() set isEdit(value: boolean) {
    this.isEdit$.next(value);
  }
  isEdit$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Input() set workflow(workflow: Workflow) {
    this.workflow$.next(workflow);
  }
  workflow$: BehaviorSubject<Workflow> = new BehaviorSubject(null);

  @Input() set hideSingleWorkflow(value: boolean) {
    this.hideSingleWorkflow$.next(value);
  }

  @Output() stepChanged = new EventEmitter<WorkflowStep>();

  hideSingleWorkflow$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  destroy$: Subject<void> = new Subject();
  selectedStepUId: string = null;
  selectedStatusName: string = null;
  selectedStatusColorCode: string = null;
  workflowStepListItems: WorkflowStep[] = [];
  readOnlyUser: boolean;
  showFieldAsEdit: boolean = false;
  showTransitionForm: boolean = false;
  selectedNextWorkflowStep: WorkflowStep;
  selectedPreviousWorkflowStep: WorkflowStep;
  hideControl: boolean = false;
  selectedWorkflow: Workflow;

  constructor(private store: Store<any>, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    combineLatest([this.workflow$, this.hostObject$, this.isEdit$, this.hideSingleWorkflow$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([workflow, hostObject]) => !!workflow && !!hostObject),
        tap(([workflow, hostObject, isEdit, hideSingleWorkflow]) => {
          this.showFieldAsEdit = isEdit;
          this.populateWorkflowStepListItems(hostObject, workflow);
          this.populateSelectedWorkflowStepValues(hostObject, workflow);
          this.calculateShouldHideFieldForSingleStep(hideSingleWorkflow);
        })
      )
      .subscribe();

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.readOnlyUser = userContext?.isReadOnlyToken;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hostObject$.complete();
    this.workflow$.complete();
    this.isEdit$.complete();
  }

  calculateShouldDisplayFieldForEdit(isEdit: boolean, dataset: Dataset) {
    this.showFieldAsEdit = !isEdit && this.workflowStepListItems?.length > 0 && !dataset?.isSubscribedRecord;
  }

  calculateShouldHideFieldForSingleStep(hideSingleWorkflow: boolean) {
    this.hideControl = this.workflowStepListItems?.length === 0 && hideSingleWorkflow;
    this.changeDetector.markForCheck();
  }

  populateSelectedWorkflowStepValues(hostObject: WorkflowHost, workflow: Workflow) {
    const step = workflow.workflowSteps.find(wfs => wfs.uId === hostObject.workflowStepUId);
    if (!!!step) {
      return;
    }

    this.selectedStepUId = step.uId;
    this.selectedStatusName = step.statusName;
    this.selectedStatusColorCode = step.statusHtmlColorCode;
    this.changeDetector.markForCheck();
  }

  populateWorkflowStepListItems(hostObject: WorkflowHost, workflow: Workflow) {
    if (!hostObject || !workflow) {
      return [];
    }

    this.selectedWorkflow = workflow;

    const listItems: WorkflowStep[] = [];

    const step = workflow?.workflowSteps?.find(ws => ws.uId === hostObject.workflowStepUId);
    if (!step) {
      return [];
    }

    const actions = step.actions.filter(a => !!a.destinationStepUId).sort((s1, s2) => s1.sortOrder - s2.sortOrder);

    actions.map(a => {
      listItems.push(workflow.workflowSteps.find(ws => ws.uId === a.destinationStepUId));
    });

    this.workflowStepListItems = listItems;
    this.changeDetector.markForCheck();
  }

  stepSelected(workflowStep: WorkflowStep): void {
    const currWfStep = this.selectedWorkflow?.workflowSteps?.find(ws => ws.uId === this.selectedStepUId);
    const nextWfStepAction = currWfStep?.actions?.find(a => a.destinationStepUId === workflowStep.uId);
    const nextWfStep = workflowStep;

    // set the control display values
    this.selectedStepUId = workflowStep.uId;
    this.selectedStatusName = workflowStep.statusName;
    this.selectedStatusColorCode = workflowStep.statusHtmlColorCode;
    this.selectedNextWorkflowStep = nextWfStep;
    this.selectedPreviousWorkflowStep = currWfStep;

    this.stepChanged.emit(workflowStep);
  }
}
